/**
 * 言語選択画面。3 言語からひとつを選び、その言語のスクリプトで録音に進む。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の言語選択画面コンポーネント
 * @graph-connects none
 */

import { useState, type ReactNode } from 'react';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { LANGUAGES, type LanguageCode, type MessagesCatalog } from '../i18n/index.js';

interface LanguageSelectPageProps {
  messages: MessagesCatalog;
  initialLanguage: LanguageCode;
  onConfirm: (language: LanguageCode) => void;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 登録対象の言語を 3 択から選ばせる画面。confirm で RecordingPage に遷移
 * @graph-connects none
 */
export function LanguageSelectPage({
  messages,
  initialLanguage,
  onConfirm,
}: LanguageSelectPageProps): ReactNode {
  const [language, setLanguage] = useState<LanguageCode>(initialLanguage);
  const { languageSelect } = messages;

  return (
    <Card>
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2.5">
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            {languageSelect.title}
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed">
            {languageSelect.description}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {LANGUAGES.map((code) => {
            const selected = language === code;
            return (
              <button
                key={code}
                type="button"
                aria-pressed={selected}
                aria-label={languageSelect.languageNames[code]}
                onClick={() => {
                  setLanguage(code);
                }}
                className={[
                  'group flex flex-col items-start gap-1 rounded-[var(--radius-xl)] p-5 text-left',
                  'ring-1 transition-all duration-[var(--duration-base)] ease-[var(--ease-out)]',
                  selected
                    ? 'bg-[var(--color-accent-soft)] ring-[var(--color-accent)] shadow-[0_0_0_4px_var(--color-accent-soft)]'
                    : 'bg-[var(--color-bg-elevated)] ring-[var(--color-border)] hover:ring-[var(--color-border-strong)] hover:bg-[var(--color-bg-hover)]',
                ].join(' ')}
              >
                <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  {code}
                </span>
                <span
                  className={[
                    'text-lg font-semibold',
                    selected ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]',
                  ].join(' ')}
                >
                  {languageSelect.languageNames[code]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button
            variant="primary"
            onClick={() => {
              onConfirm(language);
            }}
            rightIcon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            }
          >
            {languageSelect.confirm}
          </Button>
        </div>
      </div>
    </Card>
  );
}
